import api from '@/lib/axios';
import { downloadExport } from '@/services/school.api';
import type { DataTableParams, PaginatedResult } from '@/components/data-table';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export interface OrgOnlineAdmissionRow {
  id: string;
  name: string;
  gender: string | null;
  dob: string | null;
  mobile: string | null;
  whatsapp: string | null;
  fatherName: string | null;
  classId: string | null;
  className: string | null;
  sectionId: string | null;
  sectionName: string | null;
  place: string | null;
  sourceOfInfo: string | null;
  status: 'pending' | 'admitted';
  appliedAt: string;
  admittedStudentId: string | null;
  isArchivedImport: boolean;
  schoolId: string;
  schoolName: string;
  /** Read-only label — every applicant in a school shares that school's one state. Never a filter. */
  schoolState: string | null;
}

export interface ListOrgOnlineAdmissionsParams extends Partial<DataTableParams> {
  /** 'all' (default) or a specific school id belonging to this org. */
  schoolId?: string;
  className?: string;
  sessionYear?: string;
  status?: 'pending' | 'admitted';
  /** State name, e.g. "Kerala" — a school-level attribute, every applicant in a school shares its state. */
  state?: string;
}

export interface OrgOnlineAdmissionsFilters {
  classNames: string[];
  sessionYearNames: string[];
  stateNames: string[];
}

export interface OrgOnlineAdmissionsSummaryRow {
  schoolId: string;
  schoolName: string;
  total: number;
  pending: number;
  admitted: number;
  grades: { className: string; count: number }[];
}

export interface OrgOnlineAdmissionsSummaryResult {
  rows: OrgOnlineAdmissionsSummaryRow[];
  years: number[];
  selectedYear: number;
}

export interface UpdateOrgOnlineAdmissionPayload {
  name?: string;
  gender?: string;
  dob?: string;
  mobile?: string;
  whatsapp?: string;
  fatherName?: string;
  classId?: string;
  sectionId?: string;
  place?: string;
  sourceOfInfo?: string;
}

export interface ClassesSummary {
  allClassNames: string[];
  enabledClassNames: string[];
}

export interface SchoolClassRow {
  id: string;
  name: string;
  enabled: boolean;
}

export interface SchoolClassesRow {
  schoolId: string;
  schoolName: string;
  classes: SchoolClassRow[];
}

/** Online Admission — Org tier. Global Applicants List, Summary & Analytics, and Manage Classes (relocated from Platform). */
export const OrgOnlineAdmissionsApi = {
  list: async (params?: ListOrgOnlineAdmissionsParams): Promise<PaginatedResult<OrgOnlineAdmissionRow> & { truncated: boolean }> =>
    unwrap(await api.get('/org/online-admissions', { params })),

  filters: async (): Promise<OrgOnlineAdmissionsFilters> => unwrap(await api.get('/org/online-admissions/filters')),

  get: async (id: string): Promise<OrgOnlineAdmissionRow> => unwrap(await api.get(`/org/online-admissions/${id}`)),

  pdfHtml: async (id: string): Promise<string> => {
    const res = await api.get(`/org/online-admissions/${id}/pdf`, { responseType: 'text' });
    return res.data as string;
  },

  update: async (id: string, payload: UpdateOrgOnlineAdmissionPayload): Promise<OrgOnlineAdmissionRow> =>
    unwrap(await api.patch(`/org/online-admissions/${id}`, payload)),

  remove: async (id: string) => unwrap(await api.delete(`/org/online-admissions/${id}`)),

  summary: async (year?: number): Promise<OrgOnlineAdmissionsSummaryResult> =>
    unwrap(await api.get('/org/online-admissions/summary', { params: year ? { year } : undefined })),

  export: (format: 'csv' | 'xlsx' | 'pdf', params?: Pick<ListOrgOnlineAdmissionsParams, 'schoolId' | 'className' | 'sessionYear' | 'status' | 'state' | 'search'>) =>
    downloadExport('/org/online-admissions/export', 'online-admissions', format, params as Record<string, unknown>),

  // ─── Manage Classes ─────────────────────────────────────────────────────
  classesSummary: async (): Promise<ClassesSummary> => unwrap(await api.get('/org/online-admissions/manage-classes/summary')),

  listSchoolClasses: async (params?: { search?: string; page?: number; limit?: number }): Promise<{ total: number; page: number; limit: number; rows: SchoolClassesRow[] }> =>
    unwrap(await api.get('/org/online-admissions/manage-classes', { params })),

  updateSchoolClasses: async (schoolId: string, classIds: string[]) =>
    unwrap(await api.put(`/org/online-admissions/manage-classes/${schoolId}`, { classIds })),

  bulkToggle: async (className: string, action: 'on' | 'off') =>
    unwrap(await api.put('/org/online-admissions/manage-classes/bulk', { className, action })),
};
